import { FunctionComponent, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { CSSTransition } from 'react-transition-group';
import cn from 'classnames';
import { assertDefined } from '@/utils/assertDefined';
import { t } from '@/utils/i18n';
import { Button } from '@/components/common/Button';
import { useFocus } from '@/components/common/focus';
import { CaretLeft } from '@/components/common/icons/Caret';
import { CheckMark } from '@/components/common/icons/CheckMark';
import { Close } from '@/components/common/icons/Close';
import './modal.scss';

export const ModalTitle: FunctionComponent<{ children: ReactNode }> = ({ children }) => (
  <h2 className="modal__title">{children}</h2>
);

interface ModalContentProps {
  children: ReactNode;
  flex?: boolean;
}

export const ModalContent: FunctionComponent<ModalContentProps> = ({ flex, children }) => (
  <div className={cn('modal__content', { 'modal__content--flex': flex })}>{children}</div>
);

export const ModalButtons: FunctionComponent<{ children: ReactNode }> = ({ children }) => (
  <div className="modal__buttons">{children}</div>
);

interface ModalConfirmButtonsProps {
  onCancel: () => void;
  onConfirm: () => void;
}

export const ModalConfirmButtons: FunctionComponent<ModalConfirmButtonsProps> = ({
  onCancel,
  onConfirm,
}) => (
  <ModalButtons>
    <Button medium light onClick={onCancel}>
      <Close className="modal__cancel-icon" />
      {t('configuration.text.cancel')}
    </Button>
    <Button medium onClick={onConfirm}>
      <CheckMark className="modal__confirm-icon" />
      {t('configuration.text.apply')}
    </Button>
  </ModalButtons>
);

export const ModalHeader: FunctionComponent<{ children: ReactNode }> = ({ children }) => (
  <div className="modal__header">{children}</div>
);

interface ModalBackProps {
  children: ReactNode;
  onBack: () => void;
}

export const ModalHeaderBack: FunctionComponent<ModalBackProps> = ({ onBack, children }) => (
  <button className="modal__header__back" type="button" onClick={onBack}>
    <CaretLeft className="modal__header__back-icon" />
    <span className="sr-only">{children}</span>
  </button>
);

export enum ModalTypes {
  dialog = 'modal--dialog', // sheet <= 768, dialog > 768px
  panel = 'modal--panel',
  zsp = 'modal--zsp',
}

interface ModalProps {
  children: ReactNode;
  open?: boolean;
  type?: ModalTypes;
  title?: string;
  className?: string;
  preventModalFocus?: boolean;
  onEntered?: () => void;
  onClose?: () => void;
}

export const Modal: FunctionComponent<ModalProps> = ({
  open = true,
  type = ModalTypes.dialog,
  title,
  className,
  preventModalFocus,
  onEntered,
  onClose,
  children,
}) => {
  const [modal, focusOnEntered] = useFocus(open, onClose, true);

  if (!onEntered && !preventModalFocus) {
    onEntered = focusOnEntered;
  }

  return createPortal(
    <CSSTransition in={open} appear mountOnEnter unmountOnExit timeout={200} onEntered={onEntered}>
      <div className={cn('modal__outer', type, className)}>
        <div className="modal" ref={modal} tabIndex={-1} role="dialog" aria-modal>
          {children}
          {open && Boolean(onClose) && (
            <button
              type="button"
              title={title ? t('modal.close', title) : undefined}
              className="modal__close"
              onClick={onClose}
            >
              <Close className="modal__close-icon" />
            </button>
          )}
        </div>
      </div>
    </CSSTransition>,
    assertDefined(document.getElementById('modal-container'))
  );
};

interface AlertModalProps {
  children: ReactNode;
  title: string;
  message: string;
  onAccept: () => void;
}

export const AlertModal: FunctionComponent<AlertModalProps> = ({
  title,
  message,
  onAccept,
  children,
}) => (
  <Modal title={title} className="alert" onClose={onAccept}>
    <ModalHeader>
      <ModalTitle>{title}</ModalTitle>
    </ModalHeader>
    <ModalContent>{children}</ModalContent>
    <ModalButtons>
      <Button onClick={onAccept} medium>
        {message}
      </Button>
    </ModalButtons>
  </Modal>
);
